import type { SVGProps } from 'react';

/**
 * The sidebar's nav icon set - one simple stroke-based glyph per nav item,
 * picked for its meaning (grid for Dashboard, calendar for Appointments,
 * etc.). Hand-drawn with plain SVG primitives rather than pulling in an
 * icon library (lucide-react, react-icons, ...) - this app has zero UI
 * dependencies today (no component library, no date lib), and ~25 icons
 * is little enough to own directly and keep that pattern.
 */
export type NavIconName =
  | 'grid'
  | 'user'
  | 'users'
  | 'target'
  | 'columns'
  | 'checkSquare'
  | 'bell'
  | 'calendar'
  | 'messageCircle'
  | 'radio'
  | 'fileText'
  | 'zap'
  | 'gitBranch'
  | 'megaphone'
  | 'package'
  | 'shoppingCart'
  | 'clipboard'
  | 'tag'
  | 'sliders'
  | 'creditCard'
  | 'unlock'
  | 'barChart'
  | 'building'
  | 'checkCircle'
  | 'layers'
  | 'refreshCw'
  | 'alertTriangle';

const PATHS: Record<NavIconName, React.ReactNode> = {
  grid: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 21c0-3.9 2.9-7 6.5-7s6.5 3.1 6.5 7" />
      <circle cx="17.5" cy="9" r="2.8" />
      <path d="M15.3 14.2c2.6.5 4.7 2.9 4.7 6.3" />
    </>
  ),
  target: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="4.5" />
      <circle cx="12" cy="12" r="1.2" fill="currentColor" stroke="none" />
    </>
  ),
  columns: (
    <>
      <rect x="3" y="4" width="5" height="16" rx="1" />
      <rect x="9.5" y="4" width="5" height="10" rx="1" />
      <rect x="16" y="4" width="5" height="13" rx="1" />
    </>
  ),
  checkSquare: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="2.5" />
      <polyline points="7,12 10.5,15.5 17,8.5" />
    </>
  ),
  bell: (
    <>
      <path d="M6 10a6 6 0 0 1 12 0c0 5 2 6 2 6H4s2-1 2-6" />
      <path d="M10 20a2 2 0 0 0 4 0" />
    </>
  ),
  calendar: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <line x1="3" y1="10" x2="21" y2="10" />
      <line x1="7" y1="3" x2="7" y2="7" />
      <line x1="17" y1="3" x2="17" y2="7" />
    </>
  ),
  messageCircle: <path d="M21 12a8.5 8.5 0 0 1-12.4 7.5L3 21l1.6-5.3A8.5 8.5 0 1 1 21 12z" />,
  radio: (
    <>
      <circle cx="12" cy="12" r="2.3" />
      <path d="M8.5 8.5a5 5 0 0 0 0 7" />
      <path d="M15.5 8.5a5 5 0 0 1 0 7" />
      <path d="M5.6 5.6a9.5 9.5 0 0 0 0 12.8" />
      <path d="M18.4 5.6a9.5 9.5 0 0 1 0 12.8" />
    </>
  ),
  fileText: (
    <>
      <path d="M6 2.5h8l5 5V21a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1z" />
      <polyline points="14,2.5 14,8 19.5,8" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="16" y2="17" />
    </>
  ),
  zap: <polygon points="13,2 4,14 11,14 10,22 20,10 13,10" />,
  gitBranch: (
    <>
      <circle cx="6" cy="6" r="2.2" />
      <circle cx="6" cy="18" r="2.2" />
      <circle cx="18" cy="9" r="2.2" />
      <line x1="6" y1="8.2" x2="6" y2="15.8" />
      <path d="M6 8.2c0 3 2 4.8 6 4.8h4" />
    </>
  ),
  megaphone: (
    <>
      <path d="M3 10v4a1 1 0 0 0 1 1h2l5 4V5L6 9H4a1 1 0 0 0-1 1z" />
      <path d="M16 8a4 4 0 0 1 0 8" />
    </>
  ),
  package: (
    <>
      <path d="M3 8l9-5 9 5-9 5-9-5z" />
      <path d="M3 8v8l9 5 9-5V8" />
      <line x1="12" y1="13" x2="12" y2="21" />
    </>
  ),
  shoppingCart: (
    <>
      <circle cx="9" cy="20" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="18" cy="20" r="1.3" fill="currentColor" stroke="none" />
      <path d="M2.5 3h2l2.4 12.2a2 2 0 0 0 2 1.6h8.2a2 2 0 0 0 2-1.6L21 7H6" />
    </>
  ),
  clipboard: (
    <>
      <rect x="6" y="4" width="12" height="17" rx="1.5" />
      <rect x="9" y="2.3" width="6" height="3.4" rx="1" />
      <line x1="9" y1="11" x2="15" y2="11" />
      <line x1="9" y1="15" x2="15" y2="15" />
    </>
  ),
  tag: (
    <>
      <path d="M12.5 3H5a2 2 0 0 0-2 2v7.5a2 2 0 0 0 .6 1.4l9.5 9.5a2 2 0 0 0 2.8 0l6.2-6.2a2 2 0 0 0 0-2.8L12.5 3z" />
      <circle cx="8" cy="8" r="1.3" fill="currentColor" stroke="none" />
    </>
  ),
  sliders: (
    <>
      <line x1="4" y1="6" x2="20" y2="6" />
      <circle cx="9" cy="6" r="2" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <circle cx="16" cy="12" r="2" />
      <line x1="4" y1="18" x2="20" y2="18" />
      <circle cx="10" cy="18" r="2" />
    </>
  ),
  creditCard: (
    <>
      <rect x="2.5" y="5" width="19" height="14" rx="2" />
      <line x1="2.5" y1="10" x2="21.5" y2="10" />
    </>
  ),
  unlock: (
    <>
      <rect x="4.5" y="11" width="15" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 7.5-2" />
    </>
  ),
  barChart: (
    <>
      <line x1="6" y1="20" x2="6" y2="12" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="18" y1="20" x2="18" y2="14" />
    </>
  ),
  building: (
    <>
      <rect x="4" y="3" width="16" height="18" rx="1" />
      <line x1="4" y1="9" x2="20" y2="9" />
      <line x1="9" y1="3" x2="9" y2="21" />
      <rect x="11.5" y="13" width="4" height="8" />
    </>
  ),
  checkCircle: (
    <>
      <circle cx="12" cy="12" r="9" />
      <polyline points="8,12.5 11,15.5 16,9" />
    </>
  ),
  layers: (
    <>
      <polygon points="12,3 21,8 12,13 3,8" />
      <polyline points="3,13 12,18 21,13" />
      <polyline points="3,17.5 12,22.5 21,17.5" />
    </>
  ),
  refreshCw: (
    <>
      <path d="M21 8a9 9 0 0 0-15.5-5.5L3 5" />
      <polyline points="3,2 3,6 7,6" />
      <path d="M3 16a9 9 0 0 0 15.5 5.5L21 19" />
      <polyline points="21,22 21,18 17,18" />
    </>
  ),
  alertTriangle: (
    <>
      <path d="M10.3 3.9L2.4 17.5a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
      <line x1="12" y1="9.5" x2="12" y2="13.5" />
      <circle cx="12" cy="17" r="1" fill="currentColor" stroke="none" />
    </>
  ),
};

export function NavIcon({ name, ...rest }: { name: NavIconName } & SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {PATHS[name]}
    </svg>
  );
}
